// src/components/nba/NBAPlayerCard.ts
import "./NBAPlayerCard.css";

const MICHAEL_JORDAN_WIKIPEDIA_IMAGE =
  "https://images.ayesearch.com/search/c1/c174867e24053e8d718bc562dd8627a63d1203141945f09575346f44057da332.webp";

const MICHAEL_JORDAN_WIKIPEDIA_DESCRIPTION =
  "Michael Jeffrey Jordan (born February 17, 1963) is an American businessman and former professional basketball player. He played 15 seasons in the NBA, winning six championships with the Chicago Bulls and earning five league MVP awards. Jordan became one of the most recognizable athletes in the world and later moved into team ownership and business.";

const MICHAEL_JORDAN_OVERVIEW =
  "Michael Jordan is widely regarded as one of basketball's most influential players. His NBA career was defined by six championships with Chicago, elite scoring, defensive excellence, major individual awards, and a global cultural impact that extended beyond the sport.";

const MICHAEL_JORDAN_AGE = 63;

export interface NBAPlayer {
  id: number;
  name: string;
  slug: string;
  images: string[];
  youtubeVideo?: string;
  instagram?: string;
  tubetok?: string;
  tubetokThumbnail?: string;
  nbaImage: string | null;
  videoUrl?: string;
  videoThumbnail?: string;
  videoTitle?: string;
  description?: string;
  position?: string;
  teams?: string;
  hallOfFame?: string;
  wikipediaUrl?: string;
  wikipediaSnippet?: string;
  aikipediaUrl?: string;
}

function esc(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;")
    .replaceAll('"',"&quot;").replaceAll("'","&#039;");
}

function safeUrl(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "#";
  try {
    const u = new URL(raw);
    return u.protocol === "http:" || u.protocol === "https:" ? u.toString() : "#";
  } catch {
    return "#";
  }
}

function imagesFor(player: NBAPlayer): string[] {
  if (player.name.trim().toLowerCase() === "michael jordan") {
    return [
      MICHAEL_JORDAN_WIKIPEDIA_IMAGE,
      MICHAEL_JORDAN_WIKIPEDIA_IMAGE,
      MICHAEL_JORDAN_WIKIPEDIA_IMAGE,
    ];
  }

  const values = [
    player.nbaImage,
    ...(Array.isArray(player.images) ? player.images : []),
    player.tubetokThumbnail,
  ].map((v) => String(v ?? "").trim()).filter(Boolean);
  return [...new Set(values)].slice(0, 3);
}

function imageTile(url: string | undefined, label: string, cls = ""): string {
  return url
    ? `<div class="nba-media-tile ${cls}"><img src="${esc(url)}" alt="${esc(label)}" loading="lazy" /></div>`
    : `<div class="nba-media-tile nba-media-empty ${cls}"></div>`;
}

export function renderNBAPlayerCard(player: NBAPlayer, fullPage = false): string {
  const isJordan =
    player.name.trim().toLowerCase() === "michael jordan";
  const images = imagesFor(player);
  const primary = images[0] || "";

  const overviewDescription = isJordan
    ? MICHAEL_JORDAN_OVERVIEW
    : player.description ||
      `${player.name} basketball profile with indexed web results, images, reference sources and related information.`;

  const wikipediaDescription = isJordan
    ? MICHAEL_JORDAN_WIKIPEDIA_DESCRIPTION
    : player.wikipediaSnippet ||
      `Read about ${player.name}'s career, teams, achievements, playing history and impact on basketball.`;

  if (!fullPage) {
    return `
      <a href="/nba/${encodeURIComponent(player.slug)}" class="nba-card">
        ${primary ? `<img src="${esc(primary)}" class="nba-card-img" alt="${esc(player.name)}" />` : `<div class="nba-card-img nba-media-empty"></div>`}
        <h3>${esc(player.name)}</h3>
        <span>NBA player</span>
      </a>`;
  }

  const wikiUrl = safeUrl(player.wikipediaUrl || `https://en.wikipedia.org/wiki/${encodeURIComponent(player.name).replaceAll("%20","_")}`);
  const aikipediaUrl = safeUrl(player.aikipediaUrl || `https://aikipedia.org/nba/${encodeURIComponent(player.slug)}`);
  const videoUrl = safeUrl(player.videoUrl || player.youtubeVideo || player.tubetok);
  const videoThumb = isJordan
    ? MICHAEL_JORDAN_WIKIPEDIA_IMAGE
    : player.videoThumbnail || player.tubetokThumbnail || images[1] || primary;

  const questions = [
    { q:`What is ${player.name} known for?`, a:`${player.name} is known for their career and impact in professional basketball.` },
    { q:`What position did ${player.name} play?`, a:player.position || `Position information for ${player.name} can vary by team and era.` },
    { q:`Which teams did ${player.name} play for?`, a:player.teams || `Team history for ${player.name} can be explored through indexed basketball sources.` },
    { q:`Is ${player.name} in the Hall of Fame?`, a:player.hallOfFame || `Hall of Fame status depends on official induction and career eligibility.` },
  ];

  const paa = questions.map((item,i)=>`
    <div class="nba-paa-item" data-index="${i}">
      <button type="button" class="nba-paa-question"><span>${esc(item.q)}</span><span class="nba-paa-arrow">⌄</span></button>
      <div class="nba-paa-answer" hidden><p>${esc(item.a)}</p></div>
    </div>`).join("");

  return `
    <div class="nba-player-search-page">
      <section class="nba-overview-card">
        <div class="nba-overview-copy">
          <span class="nba-overview-kicker">NBA player</span>
          <h1>${esc(player.name)}</h1>
          <p class="nba-overview-description">${esc(overviewDescription)}</p>
          <div class="nba-overview-facts">
            <div><span>League</span><strong>NBA</strong></div>
            <div><span>Age</span><strong>${isJordan ? MICHAEL_JORDAN_AGE : "—"}</strong></div>
            <div><span>Position</span><strong>${esc(player.position || "Basketball player")}</strong></div>
          </div>
        </div>

        <div class="nba-media-collage">
          ${imageTile(images[0], `${player.name} image`, "nba-media-primary")}
          ${imageTile(images[1] || images[0], `${player.name} image`)}
          ${imageTile(images[2] || images[0], `${player.name} image`)}
        </div>
      </section>

      <div class="nba-results-layout">
        <section class="nba-results-column">
          <div class="nba-section-heading"><h2>Web results</h2></div>

          <article class="nba-web-result">
            <div class="nba-result-source">
              <img src="/static/icons/wiki.png" class="nba-result-favicon" alt="" onerror="this.style.display='none'" />
              <div><strong>Wikipedia</strong><span>en.wikipedia.org › wiki › ${esc(player.name.replaceAll(" ","_"))}</span></div>
            </div>
            <a href="${esc(wikiUrl)}" target="_blank" rel="noreferrer" class="nba-result-title">${esc(player.name)} - Wikipedia</a>
            <div class="nba-result-with-image">
              <p>${esc(wikipediaDescription)}</p>
              ${
                primary
                  ? `<img class="nba-reference-image" src="${esc(primary)}" alt="${esc(player.name)}" />`
                  : ""
              }
            </div>
          </article>

          <article class="nba-web-result">
            <div class="nba-result-source">
              <div class="nba-result-favicon nba-aikipedia-icon">A</div>
              <div><strong>Aikipedia</strong><span>aikipedia.org › nba › ${esc(player.slug)}</span></div>
            </div>
            <a href="${esc(aikipediaUrl)}" target="_blank" rel="noreferrer" class="nba-result-title">${esc(player.name)} basketball profile</a>
            <div class="nba-result-with-image">
              <p>Explore an Aikipedia reference profile for ${esc(player.name)} with basketball context and related information.</p>
              ${
                primary
                  ? `<img class="nba-reference-image" src="${esc(primary)}" alt="${esc(player.name)}" />`
                  : ""
              }
            </div>
          </article>

          ${
            videoUrl !== "#"
              ? `<section class="nba-video-section">
                   <div class="nba-section-heading"><h2>Video</h2></div>
                   <a href="${esc(videoUrl)}" target="_blank" rel="noreferrer" class="nba-video-card">
                     <div class="nba-video-thumbnail">
                       ${videoThumb ? `<img src="${esc(videoThumb)}" alt="${esc(player.name)} video" />` : `<div class="nba-video-empty"></div>`}
                       <span class="nba-video-play">▶</span>
                     </div>
                     <div class="nba-video-copy">
                       <span>Video</span>
                       <strong>${esc(player.videoTitle || `${player.name} highlights and coverage`)}</strong>
                       <p>Open video source ↗</p>
                     </div>
                   </a>
                 </section>`
              : ""
          }

          <section class="nba-paa-card">
            <div class="nba-section-heading"><h2>People also ask</h2></div>
            ${paa}
          </section>
        </section>

        <aside class="nba-knowledge-column">
          <section class="nba-knowledge-card">
            <div class="nba-knowledge-hero">
              ${primary ? `<img src="${esc(primary)}" alt="${esc(player.name)}" />` : `<div class="nba-knowledge-image nba-media-empty"></div>`}
              <div><h2>${esc(player.name)}</h2><span>Basketball player</span></div>
            </div>
            <div class="nba-knowledge-divider"></div>
            <dl class="nba-about-list">
              <div><dt>League</dt><dd>NBA</dd></div>
              <div><dt>Position</dt><dd>${esc(player.position || "—")}</dd></div>
              <div><dt>Teams</dt><dd>${esc(player.teams || "—")}</dd></div>
              <div><dt>Hall of Fame</dt><dd>${esc(player.hallOfFame || "—")}</dd></div>
            </dl>
          </section>
        </aside>
      </div>
    </div>`;
}

export function initNBAPlayerCardEvents(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>(".nba-paa-item").forEach((item) => {
    const button = item.querySelector<HTMLButtonElement>(".nba-paa-question");
    const answer = item.querySelector<HTMLElement>(".nba-paa-answer");
    const arrow = item.querySelector<HTMLElement>(".nba-paa-arrow");
    if (!button || !answer || !arrow) return;

    button.addEventListener("click", () => {
      const open = answer.hidden;
      container.querySelectorAll<HTMLElement>(".nba-paa-answer").forEach((el) => { el.hidden = true; });
      container.querySelectorAll<HTMLElement>(".nba-paa-arrow").forEach((el) => { el.textContent = "⌄"; });
      if (open) { answer.hidden = false; arrow.textContent = "⌃"; }
    });
  });
}
